import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  getListNotificationsQueryKey,
  getGetNotificationUnreadCountQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCheck, BellOff, Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const TYPES = [
  { value: undefined, label: "All" },
  { value: "approval" as const, label: "Approvals" },
  { value: "mention" as const, label: "Mentions" },
  { value: "renewal" as const, label: "Renewals" },
  { value: "expiring_contract" as const, label: "Expiring Contracts" },
  { value: "expiring_insurance" as const, label: "Expiring Insurance" },
  { value: "billing" as const, label: "Billing" },
  { value: "system" as const, label: "System" },
] as const;

const TYPE_COLORS: Record<string, string> = {
  approval: "bg-blue-500/10 text-blue-700 border-blue-200",
  mention: "bg-violet-500/10 text-violet-700 border-violet-200",
  renewal: "bg-amber-500/10 text-amber-700 border-amber-200",
  expiring_contract: "bg-orange-500/10 text-orange-700 border-orange-200",
  expiring_insurance: "bg-red-500/10 text-red-700 border-red-200",
  billing: "bg-rose-500/10 text-rose-700 border-rose-200",
  system: "bg-gray-500/10 text-gray-700 border-gray-200",
};

type NotifType = "approval" | "mention" | "renewal" | "expiring_contract" | "expiring_insurance" | "billing" | "system";

export default function Notifications() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<NotifType | undefined>(undefined);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const queryKey = getListNotificationsQueryKey({
    type: selectedType,
    unreadOnly: unreadOnly || undefined,
    limit,
    offset,
  });

  const { data, isLoading } = useListNotifications(
    {
      type: selectedType,
      unreadOnly: unreadOnly || undefined,
      limit,
      offset,
    },
    { query: { queryKey } },
  );

  const markAllRead = useMarkAllNotificationsRead();
  const markOneRead = useMarkNotificationRead();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetNotificationUnreadCountQueryKey() });
  };

  const handleClick = async (n: { id: number; link: string | null; readAt: string | null }) => {
    const isExternal = !!n.link && (
      n.link.startsWith("http://") || n.link.startsWith("https://") || n.link.startsWith("/api/")
    );
    if (!n.readAt) {
      if (isExternal) {
        // Await mark-read so it completes before page unload
        await markOneRead.mutateAsync({ id: n.id }).catch(() => undefined);
        invalidateAll();
      } else {
        markOneRead.mutate({ id: n.id }, { onSuccess: invalidateAll });
      }
    }
    if (n.link) {
      if (isExternal) {
        window.location.assign(n.link);
      } else {
        setLocation(n.link);
      }
    }
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate(undefined, { onSuccess: invalidateAll });
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const unreadCount = items.filter((n) => !n.readAt).length;

  const handleTypeChange = (type: NotifType | undefined) => {
    setSelectedType(type);
    setOffset(0);
  };

  const handleUnreadToggle = () => {
    setUnreadOnly((v) => !v);
    setOffset(0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-6 w-6 text-muted-foreground" />
            Notifications
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your complete notification history for approvals, mentions, renewals, and more.
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="flex-shrink-0 gap-2"
            onClick={handleMarkAllRead}
            disabled={markAllRead.isPending}
          >
            <CheckCheck className="h-4 w-4" />
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {TYPES.map((t) => (
            <button
              key={String(t.value)}
              onClick={() => handleTypeChange(t.value as NotifType | undefined)}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                selectedType === t.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background border-border hover:bg-muted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleUnreadToggle}
          className={`ml-auto px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
            unreadOnly
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background border-border hover:bg-muted"
          }`}
        >
          Unread only
        </button>
      </div>

      {/* List */}
      <div className="rounded-lg border overflow-hidden">
        {isLoading ? (
          <div className="divide-y">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-4 flex gap-3">
                <Skeleton className="w-2 h-2 rounded-full mt-2 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 flex flex-col items-center gap-3 text-center text-muted-foreground">
            <BellOff className="h-10 w-10 opacity-30" />
            <p className="font-medium">
              {unreadOnly ? "No unread notifications" : "No notifications yet"}
            </p>
            <p className="text-sm max-w-xs">
              {unreadOnly
                ? "You're all caught up! Toggle 'Unread only' off to see your history."
                : "Notifications for approvals, mentions, renewals, and more will appear here."}
            </p>
          </div>
        ) : (
          <ul className="divide-y">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => handleClick(n)}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors flex items-start gap-3 ${!n.readAt ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}`}
                >
                  <span
                    className={`mt-2 flex-shrink-0 w-2 h-2 rounded-full transition-colors ${!n.readAt ? "bg-blue-500" : "bg-transparent"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge
                        variant="outline"
                        className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 ${TYPE_COLORS[n.type] ?? TYPE_COLORS.system}`}
                      >
                        {n.type.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className={`text-sm leading-snug ${!n.readAt ? "font-semibold" : "font-medium"}`}>
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
